import { useState } from "react";
import { toast } from "../Toast";
import { API_URL } from "../config/env";
import { COMING_FEATURES } from "../config/constants";

export default function ComingSoonPage({ userEmail, subscription, userId }) {
  const [votes, setVotes] = useState(() => {
    const v = {};
    COMING_FEATURES.forEach(f => {
      const key = `pitcherly_votes_${f.name.replace(/\s+/g, '_').toLowerCase()}`;
      v[f.name] = parseInt(localStorage.getItem(key) || "0", 10);
    });
    return v;
  });
  const [voted, setVoted] = useState(() => {
    const v = {};
    COMING_FEATURES.forEach(f => {
      const key = `pitcherly_voted_${f.name.replace(/\s+/g, '_').toLowerCase()}`;
      v[f.name] = localStorage.getItem(key) === "1";
    });
    return v;
  });
  const [request, setRequest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const effectiveStatus = subscription?.stripe_status;
  const currentPlan = effectiveStatus === "trialing" ? "trial" : subscription?.plan === "pro" && effectiveStatus === "active" ? "pro" : subscription?.plan === "lite" && effectiveStatus === "active" ? "lite" : effectiveStatus === "active" ? "starter" : "trial";

  const handlePlanUpgrade = async (plan) => {
    setUpgrading(true);
    try {
      const res = await fetch(`${API_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, user_id: userId, user_email: subscription?.email || "" }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else toast.error(data.detail || "Failed to create checkout");
    } catch { toast.error("Could not connect to server"); }
    setUpgrading(false);
  };

  const handleVote = (name) => {
    const key = `pitcherly_votes_${name.replace(/\s+/g, '_').toLowerCase()}`;
    const votedKey = `pitcherly_voted_${name.replace(/\s+/g, '_').toLowerCase()}`;
    if (voted[name]) return;
    const newCount = (votes[name] || 0) + 1;
    localStorage.setItem(key, String(newCount));
    localStorage.setItem(votedKey, "1");
    setVotes(prev => ({ ...prev, [name]: newCount }));
    setVoted(prev => ({ ...prev, [name]: true }));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!request.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/feature-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feature: request.trim(), user_email: userEmail }),
      });
      setSubmitted(true);
      setRequest("");
      setTimeout(() => setSubmitted(false), 4000);
    } catch (e) { console.warn("Feature request submit failed:", e); }
    setSubmitting(false);
  };

  const check = <span style={{color:'#22c55e',fontWeight:600}}>✓</span>;
  const cross = <span style={{color:'var(--text3)'}}>—</span>;

  const PLAN_FEATURES = [
    { name: "AI Generations", trial: "50 total", lite: "50/month", starter: "200/month", pro: "Unlimited" },
    { name: "MLS Listing Generator", trial: check, lite: check, starter: check, pro: check },
    { name: "Listing Tools", trial: check, lite: check, starter: check, pro: check },
    { name: "Follow-Up Emails", trial: check, lite: check, starter: check, pro: check },
    { name: "Open House Follow-Up", trial: check, lite: check, starter: check, pro: check },
    { name: "Seller Update Emails", trial: check, lite: check, starter: check, pro: check },
    { name: "Contract Summarizer", trial: check, lite: check, starter: check, pro: check },
    { name: "Agent Bio Generator", trial: check, lite: check, starter: check, pro: check },
    { name: "Expired Listing Outreach", trial: check, lite: check, starter: check, pro: check },
    { name: "FSBO Outreach", trial: check, lite: check, starter: check, pro: check },
    { name: "CMA Generator", trial: check, lite: check, starter: check, pro: check },
    { name: "Contacts & CSV Import", trial: check, lite: check, starter: check, pro: check },
    { name: "Email Export (Email/Gmail/Outlook)", trial: check, lite: check, starter: check, pro: check },
    { name: "Property Info (AI Estimated)", trial: check, lite: check, starter: check, pro: check },
    { name: "Verified Property Lookups (RentCast)", trial: "Not available", lite: "Not available", starter: "10/month", pro: "50/month" },
    { name: "Market-Data CMAs (live estimate + comps)", trial: cross, lite: cross, starter: check, pro: check },
    { name: "Priority Generation Speed", trial: cross, lite: cross, starter: cross, pro: check },
  ];

  const colStyle = (plan) => ({
    padding: '10px 8px',
    textAlign: 'center',
    fontSize: 13,
    color: '#e8e4dd',
    borderBottom: '1px solid #1f1f1f',
    ...(plan === 'pro' ? { background: 'rgba(212,145,92,0.04)' } : {}),
  });

  const headStyle = (plan) => ({
    padding: '16px 8px 8px',
    textAlign: 'center',
    borderBottom: '1px solid #2a2a2a',
    ...(plan === 'pro' ? { background: 'rgba(212,145,92,0.06)', borderLeft: '1px solid var(--accent)', borderRight: '1px solid var(--accent)' } : {}),
  });

  return (
    <div className="coming-page">
      {/* PLAN COMPARISON */}
      <div className="coming-header">
        <div className="coming-title">Plans & Features</div>
        <div className="coming-sub">Compare plans and find the right fit for your business.</div>
      </div>

      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,overflowX:'auto',WebkitOverflowScrolling:'touch',marginBottom:32}}>
        <table style={{width:'100%',minWidth:560,borderCollapse:'collapse',fontFamily:"'Geist', sans-serif"}}>
          <thead>
            <tr>
              <th style={{padding:'16px 14px',textAlign:'left',fontSize:12,color:'var(--text3)',fontWeight:600,letterSpacing:'0.05em',textTransform:'uppercase',borderBottom:'1px solid #2a2a2a',width:'40%'}}>Feature</th>
              <th style={headStyle('trial')}>
                <div style={{fontSize:14,fontWeight:600,color:'var(--text)'}}>Free Trial</div>
                <div style={{fontSize:12,color:'var(--text2)',marginTop:2}}>7 days</div>
                {currentPlan === 'trial' && <div style={{display:'inline-block',marginTop:6,padding:'2px 8px',borderRadius:4,fontSize:10,fontWeight:600,background:'rgba(245,158,11,0.15)',color:'#f59e0b'}}>CURRENT PLAN</div>}
              </th>
              <th style={headStyle('lite')}>
                <div style={{fontSize:14,fontWeight:600,color:'var(--text)'}}>Lite</div>
                <div style={{fontSize:12,color:'var(--text2)',marginTop:2}}>$19/mo</div>
                {currentPlan === 'lite' && <div style={{display:'inline-block',marginTop:6,padding:'2px 8px',borderRadius:4,fontSize:10,fontWeight:600,background:'rgba(34,197,94,0.15)',color:'#22c55e'}}>CURRENT PLAN</div>}
              </th>
              <th style={headStyle('starter')}>
                <div style={{fontSize:14,fontWeight:600,color:'var(--text)'}}>Starter</div>
                <div style={{fontSize:12,color:'var(--text2)',marginTop:2}}>$49/mo</div>
                {currentPlan === 'starter' && <div style={{display:'inline-block',marginTop:6,padding:'2px 8px',borderRadius:4,fontSize:10,fontWeight:600,background:'rgba(34,197,94,0.15)',color:'#22c55e'}}>CURRENT PLAN</div>}
              </th>
              <th style={{...headStyle('pro'), borderTop: '2px solid var(--accent)', borderRadius: '0'}}>
                <div style={{fontSize:14,fontWeight:600,color:'var(--accent)'}}>Pro</div>
                <div style={{fontSize:12,color:'var(--text2)',marginTop:2}}>$99/mo</div>
                {currentPlan === 'pro' && <div style={{display:'inline-block',marginTop:6,padding:'2px 8px',borderRadius:4,fontSize:10,fontWeight:600,background:'rgba(212,145,92,0.15)',color:'var(--accent)'}}>CURRENT PLAN</div>}
              </th>
            </tr>
          </thead>
          <tbody>
            {PLAN_FEATURES.map((f, i) => (
              <tr key={i}>
                <td style={{padding:'10px 14px',fontSize:13,color:'var(--text)',borderBottom:'1px solid #1f1f1f'}}>{f.name}</td>
                <td style={colStyle('trial')}>{f.trial}</td>
                <td style={colStyle('lite')}>{f.lite}</td>
                <td style={colStyle('starter')}>{f.starter}</td>
                <td style={{...colStyle('pro'), borderLeft:'1px solid rgba(212,145,92,0.15)', borderRight:'1px solid rgba(212,145,92,0.15)'}}>{typeof f.pro === 'string' ? <span style={{color:'var(--accent)',fontWeight:600}}>{f.pro}</span> : f.pro}</td>
              </tr>
            ))}
            <tr>
              <td style={{padding:'16px 14px',borderBottom:'none'}} />
              <td style={{padding:'16px 8px',textAlign:'center',borderBottom:'none'}}>
                {currentPlan === 'trial' && <div style={{fontSize:12,color:'var(--text3)',fontStyle:'italic'}}>Active</div>}
              </td>
              <td style={{padding:'16px 8px',textAlign:'center',borderBottom:'none'}}>
                {currentPlan !== 'lite' && currentPlan !== 'starter' && currentPlan !== 'pro' ? (
                  <button onClick={() => handlePlanUpgrade('lite')} disabled={upgrading} style={{padding:'10px 20px',background:'transparent',border:'1px solid var(--text3)',color:'var(--text)',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif",width:'100%'}}>{upgrading ? 'Redirecting...' : 'Get Lite'}</button>
                ) : currentPlan === 'lite' ? (
                  <div style={{fontSize:12,color:'var(--text3)',fontStyle:'italic'}}>Active</div>
                ) : null}
              </td>
              <td style={{padding:'16px 8px',textAlign:'center',borderBottom:'none'}}>
                {currentPlan !== 'starter' && currentPlan !== 'pro' ? (
                  <button onClick={() => handlePlanUpgrade('starter')} disabled={upgrading} style={{padding:'10px 20px',background:'transparent',border:'1px solid var(--accent)',color:'var(--accent)',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif",width:'100%'}}>{upgrading ? 'Redirecting...' : 'Get Starter'}</button>
                ) : currentPlan === 'starter' ? (
                  <div style={{fontSize:12,color:'var(--text3)',fontStyle:'italic'}}>Active</div>
                ) : null}
              </td>
              <td style={{padding:'16px 8px',textAlign:'center',borderBottom:'none',borderLeft:'1px solid rgba(212,145,92,0.15)',borderRight:'1px solid rgba(212,145,92,0.15)'}}>
                {currentPlan !== 'pro' ? (
                  <button onClick={() => handlePlanUpgrade('pro')} disabled={upgrading} style={{padding:'10px 20px',background:'var(--accent)',border:'none',color:'#fff',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:"'Geist', sans-serif",width:'100%'}}>{upgrading ? 'Redirecting...' : 'Get Pro'}</button>
                ) : (
                  <div style={{fontSize:12,color:'var(--text3)',fontStyle:'italic'}}>Active</div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* COMING SOON SECTION */}
      <div className="coming-header" style={{marginTop:16}}>
        <div className="coming-title">What's Coming to Pitcher<em style={{color:"var(--accent)",fontStyle:"italic"}}>ly</em></div>
        <div className="coming-sub">We're building these features based on what agents need most. Vote for what matters to you.</div>
      </div>
      <div className="coming-grid">
        {COMING_FEATURES.map(f => (
          <div key={f.name} className="coming-card">
            <div className="coming-card-top">
              <div className="coming-card-name">{f.name}</div>
              <span className={`coming-badge ${f.badgeCls}`}>{f.badge}</span>
            </div>
            <div className="coming-card-desc">{f.desc}</div>
            <button className={`coming-vote ${voted[f.name] ? "voted" : ""}`} onClick={() => handleVote(f.name)}>
              👍 {votes[f.name] || 0}
            </button>
          </div>
        ))}
      </div>
      <div className="coming-request">
        <div className="coming-request-title">Have a feature request?</div>
        <div className="coming-request-sub">Tell us what would save you the most time.</div>
        {submitted && <div className="auth-success" style={{marginBottom:12}}>Thanks! We'll review your request.</div>}
        <form onSubmit={handleSubmitRequest} className="coming-request-row">
          <input value={request} onChange={e => setRequest(e.target.value)} placeholder="e.g. Auto-generate Instagram captions from listings..." disabled={submitting} />
          <button className="coming-request-btn" type="submit" disabled={submitting || !request.trim()}>
            {submitting ? "Sending..." : "Submit Request"}
          </button>
        </form>
      </div>
    </div>
  );
}
